import React, { MouseEventHandler } from "react";

import { useChatClass } from "../../theme";
import { Button } from "./styled";

export interface LauncherProps {
  /** An image URL to be rendered as the icon.
   * Defaults to the "launch" SVG if not provided. */
  image?: string;
  /** A callback that will be executed when the button is clicked.
   * Typically used to open the chat widget */
  onClick: MouseEventHandler<HTMLButtonElement>;
  /** Size in pixels
   * @default 60 */
  size?: number;
}

/** A floating action button used to launch the chat widget.  */
export const Launcher: React.FC<LauncherProps> = ({
  image,
  onClick,
  size = 60,
}) => {
  const chatClass = useChatClass();
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick(event as any);
    }
  };

  // Open on touchend, not just click, so the first tap works even on host pages
  // that swallow the synthetic click of that tap — e.g. WordPress sites running
  // WP Rocket's "Delay JavaScript Execution", or carousel/slider libraries that
  // attach non-passive touchstart listeners. touchend still fires in those cases,
  // and preventDefault() cancels the emulated click so this never double-fires
  // with onClick below.
  const handleTouchEnd = (event: React.TouchEvent<HTMLButtonElement>) => {
    event.preventDefault();
    onClick(event as any);
  };

  return (
    <Button
      onClick={onClick}
      onTouchEnd={handleTouchEnd}
      onKeyDown={handleKeyDown}
      $size={size}
      style={image ? { padding: 0, borderWidth: 0 } : { padding: 12 }}
      className={chatClass("launcher")}
      aria-label="Open chat widget"
      role="button"
      tabIndex={0}
    >
      {image ? (
        <img src={image} alt="" aria-hidden="true" />
      ) : (
        <DefaultLauncher size={size} />
      )}
    </Button>
  );
};

const DefaultLauncher = ({ size }: { size: number }) => (
  <svg
    height={Math.floor(size * 0.65)}
    width={Math.floor(size * 0.65)}
    viewBox="0 0 36 36"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <g>
      <path
        d="M18.0000533,7 C24.6266329,7 30,11.4789312 30,16.9976931 C30,22.5163617 24.6266329,26.9953062 18.0000533,26.9953062 C17.123351,26.9971724 16.2483812,26.9169271 15.386606,26.7553699 C14.0404188,27.7431078 12.5315125,28.4873102 10.9284053,28.9541197 C10.4583473,29.0903502 9.95341047,28.916663 9.66660965,28.5199682 C9.37982216,28.1234068 9.37297168,27.5894152 9.64952342,27.1855224 C10.1505552,26.5172998 10.5515886,25.7796289 10.840002,24.9957036 C7.9365286,23.3624038 6.10015838,20.3278759 6,16.9976931 C6,11.4789179 11.3733271,7 18.0000533,7 Z M18.0000533,18.0020932 L14.0000889,18.0020932 L13.8644511,18.0112196 C13.3765531,18.0774186 13.0005042,18.4957012 13.0005042,19.0018279 C13.0005042,19.5539661 13.4480335,20.0015625 14.0000889,20.0015625 L18.0000533,20.0015625 L18.135691,19.9924361 C18.623589,19.9262371 18.9996379,19.5079545 18.9996379,19.0018279 C18.9996379,18.4496896 18.5521087,18.0020932 18.0000533,18.0020932 Z M22.0001244,14.001515 L14.0000889,14.001515 L13.8644511,14.0106414 C13.3765531,14.0768404 13.0005042,14.495123 13.0005042,15.0012497 C13.0005042,15.5533879 13.4480335,16.0009843 14.0000889,16.0009843 L22.0001244,16.0009843 L22.1357621,15.9918579 C22.6236601,15.9256589 22.999709,15.5073764 22.999709,15.0012497 C22.999709,14.4491115 22.5521797,14.001515 22.0001244,14.001515 Z"
        fill="#ffffff"
      ></path>
    </g>
  </svg>
);
